import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import FoodPremisesCMS from './FoodPremisesCMS';

const Wrap = styled.div`
  display: grid; grid-template-columns: 260px 1fr; gap: 20px;
  @media (max-width: 1000px){ grid-template-columns: 1fr; }
`;
const Side = styled.aside`
  background: #fff; border: 1px solid ${({ theme }) => theme.colors.gray[200]}; border-radius: 12px; padding: 14px;
`;
const Main = styled.main``;
const Top = styled.div`
  display:flex; justify-content: space-between; align-items:center; margin-bottom: 14px;
`;
const Title = styled.h1` margin:0; color: ${({ theme }) => theme.colors.green[700]}; `;
const Button = styled.button`
  padding: 10px 14px; border:1px solid ${({ theme }) => theme.colors.gray[300]}; background: ${({ theme }) => theme.colors.white}; border-radius: 10px; cursor: pointer;
`;
const Primary = styled(Button)`
  background: ${({ theme }) => theme.colors.green[600]}; color:#fff; border: none;
  &:disabled { opacity: 0.6; cursor: default; }
`;
const Danger = styled(Button)`
  background: #dc2626; color:#fff; border: none;
`;
const Menu = styled.div` display:flex; flex-direction: column; gap: 6px; `;
const Item = styled.button`
  padding: 10px 12px; text-align: left; border:none; border-radius: 8px; cursor:pointer; width: 100%;
  background: ${({ $active, theme }) => $active ? theme.colors.green[50] : '#fff' };
  color: ${({ $active, theme }) => $active ? theme.colors.green[800] : theme.colors.text };
`;
const Card = styled.div`
  background: #fff; border: 1px solid ${({ theme }) => theme.colors.gray[200]}; border-radius: 12px; padding: 16px; margin-bottom: 16px;
`;
const Grid = styled.div` display:grid; grid-template-columns: repeat(auto-fit, minmax(260px,1fr)); gap: 12px; `;
const Field = styled.div` margin-bottom: 10px; label{display:block; margin-bottom:6px;} input, textarea, select{ width:100%; padding:10px; border:1px solid ${({ theme }) => theme.colors.gray[300]}; border-radius: 8px; } textarea{ min-height: 100px; }
`;
const Row = styled.div` display:flex; gap:10px; align-items:center; flex-wrap: wrap; `;
const Img = styled.img.attrs({ loading: 'lazy', decoding: 'async' })` height: 64px; width: 96px; object-fit: cover; border-radius: 8px; border:1px solid ${({ theme }) => theme.colors.gray[200]}; `;

const tabs = [
  'Users','Food Premises','Home','About','Objectives','News','Team','Services','Gallery','Partnerships','Careers','FA Act','Contact'
];

const ImagePicker = ({ onPick }) => {
  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const res = await api.upload.image(file);
    onPick(res.base64);
    e.target.value = '';
  };
  return <input type="file" accept="image/*" onChange={onChange} />;
};

const NewsModule = () => {
  const [header, setHeader] = useState(null);
  const [posts, setPosts] = useState([]);
  const [message, setMessage] = useState(null);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [savingHeader, setSavingHeader] = useState(false);
  const [creatingPost, setCreatingPost] = useState(false);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [deletingImageKey, setDeletingImageKey] = useState(null);
  const [savingPostId, setSavingPostId] = useState(null);

  const load = async () => {
    const [h, list] = await Promise.all([
      api.news.getHeader(),
      api.news.listPosts({ page, limit })
    ]);
    setHeader(h);
    setPosts(list.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [page]);

  if (!header) return <div>Loading…</div>;

  const saveHeader = async () => {
    setMessage(null);
    setSavingHeader(true);
    try {
      const d = await api.news.updateHeader(header);
      setHeader(d);
      setMessage('Header saved');
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to save header');
    } finally {
      setSavingHeader(false);
    }
  };

  const createPost = async () => {
    setMessage(null);
    setCreatingPost(true);
    try {
      const p = await api.news.createPost({ title: 'Untitled', description: '', images: [] });
      setPosts(prev => [p, ...prev]);
      setMessage('Post created');
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to create post');
    } finally {
      setCreatingPost(false);
    }
  };

  const updatePostField = (id, field, value) => {
    setPosts(prev => prev.map(p => p._id === id ? { ...p, [field]: value } : p));
  };

  const savePost = async (id) => {
    const post = posts.find(p => p._id === id);
    if (!post) return;
    setSavingPostId(id);
    setMessage(null);
    try {
      const d = await api.news.updatePost(id, {
        title: post.title,
        description: post.description,
        images: post.images || []
      });
      setPosts(prev => prev.map(p => p._id === id ? d : p));
      setMessage('Post saved');
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to save post');
    } finally {
      setSavingPostId(null);
    }
  };

  const addPostImage = async (id, base64) => {
    setPosts(prev => prev.map(p => p._id === id ? {
      ...p,
      images: [...(p.images || []), base64]
    } : p));
  };

  const removePostImage = async (id, index) => {
    const key = `${id}-${index}`;
    setDeletingImageKey(key);
    try {
      await api.news.deletePostImage(id, index);
      await load();
    } finally {
      setDeletingImageKey(null);
    }
  };

  const deletePost = async (id) => {
    setDeletingPostId(id);
    try {
      await api.news.deletePost(id);
      setPosts(prev => prev.filter(p => p._id !== id));
    } finally {
      setDeletingPostId(null);
    }
  };

  return (
    <>
      <Card>
        <h2>News Page Header</h2>
        <Grid>
          <Field><label>Title</label><input value={header.title || ''} onChange={e=>setHeader({ ...header, title: e.target.value })} /></Field>
          <Field><label>Description</label><textarea value={header.description || ''} onChange={e=>setHeader({ ...header, description: e.target.value })} /></Field>
        </Grid>
        {header.image ? <Img src={header.image} alt="header" /> : null}
        <Row style={{ marginTop: 8 }}>
          <ImagePicker onPick={(b)=> setHeader({ ...header, image: b })} />
        </Row>
        <Row style={{ marginTop: 10 }}>
          <Primary onClick={saveHeader} disabled={savingHeader}>
            {savingHeader ? 'Saving…' : 'Save Header'}
          </Primary>
          {message ? <div>{message}</div> : null}
        </Row>
      </Card>

      <Card>
        <h2>Posts</h2>
        <Row style={{ marginBottom: 10 }}>
          <Primary onClick={createPost} disabled={creatingPost}>
            {creatingPost ? 'Creating…' : 'Create Post'}
          </Primary>
          {message && !savingHeader && <div>{message}</div>}
        </Row>
        <Grid>
          {posts.map(p => (
            <Card key={p._id}>
              <Field><label>Title</label><input value={p.title || ''} onChange={e=>updatePostField(p._id, 'title', e.target.value)} /></Field>
              <Field><label>Description</label><textarea value={p.description || ''} onChange={e=>updatePostField(p._id, 'description', e.target.value)} /></Field>
              <Row>
                {(p.images || []).map((src, idx) => (
                  <div key={idx} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
                    <Img src={src} alt={`${p.title} ${idx+1}`} />
                    <Danger
                      onClick={()=>removePostImage(p._id, idx)}
                      disabled={deletingImageKey === `${p._id}-${idx}`}
                    >
                      {deletingImageKey === `${p._id}-${idx}` ? 'Removing…' : 'Remove'}
                    </Danger>
                  </div>
                ))}
              </Row>
              <Row>
                <ImagePicker onPick={(b)=>addPostImage(p._id, b)} />
              </Row>
              <Row>
                <Primary
                  onClick={()=>savePost(p._id)}
                  disabled={savingPostId === p._id}
                >
                  {savingPostId === p._id ? 'Saving…' : 'Save Post'}
                </Primary>
                <Danger
                  onClick={()=>deletePost(p._id)}
                  disabled={deletingPostId === p._id}
                >
                  {deletingPostId === p._id ? 'Deleting…' : 'Delete Post'}
                </Danger>
              </Row>
            </Card>
          ))}
        </Grid>
      </Card>
    </>
  );
};

const ServicesModule = () => {
  const [doc, setDoc] = useState(null);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.services.getPage().then(setDoc);
  useEffect(() => { load(); }, []);

  const save = async () => {
    setMessage(null);
    setSaving(true);
    try {
      const data = await api.services.updatePage({
        title: doc.title || '',
        intro: doc.intro || '',
        services: (doc.services || []).map((item) => ({
          title: item.title || '',
          description: item.description || ''
        }))
      });
      setDoc(data);
      setMessage('Changes saved');
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const addService = () => setDoc(prev => ({
    ...prev,
    services: [ ...(prev?.services || []), { title: '', description: '' } ]
  }));

  const updateService = (index, field, value) => setDoc(prev => ({
    ...prev,
    services: (prev?.services || []).map((item, idx) => idx === index ? { ...item, [field]: value } : item)
  }));

  const removeService = (index) => setDoc(prev => ({
    ...prev,
    services: (prev?.services || []).filter((_, idx) => idx !== index)
  }));

  if (!doc) return <div>Loading…</div>;

  return (
    <>
      <Card>
        <h2>Services Page</h2>
        <Grid>
          <Field><label>Page Title</label><input value={doc.title || ''} onChange={e=>setDoc({ ...doc, title: e.target.value })} /></Field>
        </Grid>
        <Field><label>Intro</label><textarea value={doc.intro || ''} onChange={e=>setDoc({ ...doc, intro: e.target.value })} /></Field>
      </Card>

      <Card>
        <h2>Service List</h2>
        <Row style={{ marginBottom: 10 }}>
          <Primary onClick={addService}>Add Service</Primary>
        </Row>
        <Grid>
          {(doc.services || []).map((service, index) => (
            <Card key={`service-${index}`}>
              <Field><label>Service Title</label><input value={service.title || ''} onChange={e=>updateService(index, 'title', e.target.value)} /></Field>
              <Field><label>Description</label><textarea value={service.description || ''} onChange={e=>updateService(index, 'description', e.target.value)} /></Field>
              <Row>
                <Danger onClick={()=>removeService(index)}>Delete Service</Danger>
              </Row>
            </Card>
          ))}
        </Grid>
      </Card>

      <Row>
        <Primary onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Primary>
        {message ? <div>{message}</div> : null}
      </Row>
    </>
  );
 };

 const TeamModule = () => {
  const [page, setPage] = useState(null);
  const [members, setMembers] = useState([]);
  const [pageMessage, setPageMessage] = useState(null);
  const [memberMessage, setMemberMessage] = useState(null);
  const [savingPage, setSavingPage] = useState(false);
  const [savingMemberId, setSavingMemberId] = useState(null);
  const [removingMemberId, setRemovingMemberId] = useState(null);

  const load = async () => {
    const [p, m] = await Promise.all([api.team.getPage(), api.team.listMembers()]);
    setPage(p);
    setMembers(m);
  };
  useEffect(() => { load(); }, []);

  if (!page) return <div>Loading…</div>;

  const savePage = async () => {
    setPageMessage(null);
    setSavingPage(true);
    try {
      const d = await api.team.updatePage(page);
      setPage(d);
      setPageMessage('Changes saved');
    } catch (err) {
      setPageMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to save changes');
    } finally {
      setSavingPage(false);
    }
  };

  const createDraft = () => {
    setMemberMessage(null);
    const tempId = `temp-${Date.now()}`;
    setMembers(prev => [
      { tempId, name: '', role: '', bio: '', image: '', section: 'management', isNew: true },
      ...prev
    ]);
  };

  const updateMemberField = (id, field, value) => {
    setMembers(prev => prev.map(m => (
      m._id === id || m.tempId === id ? { ...m, [field]: value } : m
    )));
  };

  const saveMember = async (id) => {
    const mem = members.find(m => m._id === id || m.tempId === id);
    if (!mem) return;
    setSavingMemberId(id);
    setMemberMessage(null);
    try {
      const payload = {
        name: mem.name || '',
        role: mem.role || '',
        bio: mem.bio || '',
        image: mem.image || '',
        section: mem.section || 'management'
      };
      const d = mem._id
        ? await api.team.updateMember(mem._id, payload)
        : await api.team.createMember(payload);
      setMembers(prev => prev.map(m => (
        m._id === id || m.tempId === id ? d : m
      )));
      setMemberMessage(mem._id ? 'Member saved' : 'Member created');
    } catch (err) {
      setMemberMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to save member');
    } finally {
      setSavingMemberId(null);
    }
  };

  const removeMember = async (id) => {
    const mem = members.find(m => m._id === id || m.tempId === id);
    if (!mem) return;
    if (!mem._id) {
      setMembers(prev => prev.filter(m => m.tempId !== id));
      return;
    }
    setRemovingMemberId(id);
    setMemberMessage(null);
    try {
      await api.team.deleteMember(mem._id);
      setMembers(prev => prev.filter(m => m._id !== mem._id));
      setMemberMessage('Member deleted');
    } finally {
      setRemovingMemberId(null);
    }
  };

  return (
    <>
      <Card>
        <h2>Team Page</h2>
        <Field><label>Description</label><textarea value={page.description || ''} onChange={e=>setPage({ ...page, description: e.target.value })} /></Field>
        <Row style={{ marginTop:10 }}>
          <Primary onClick={savePage} disabled={savingPage}>
            {savingPage ? 'Saving…' : 'Save Page'}
          </Primary>
          {pageMessage ? <div>{pageMessage}</div> : null}
        </Row>
      </Card>

      <Card>
        <h2>Members</h2>
        <Row style={{ marginBottom: 10 }}>
          <Primary onClick={createDraft}>Add Member</Primary>
        </Row>
        {memberMessage ? <div>{memberMessage}</div> : null}
        <Grid>
          {members.map(m => {
            const memberKey = m._id || m.tempId;
            return (
              <Card key={memberKey}>
                <Field><label>Name</label><input value={m.name || ''} onChange={e=>updateMemberField(memberKey, 'name', e.target.value)} /></Field>
                <Field><label>Role</label><input value={m.role || ''} onChange={e=>updateMemberField(memberKey, 'role', e.target.value)} /></Field>
                <Field>
                  <label>Section</label>
                  <select value={m.section || 'management'} onChange={e=>updateMemberField(memberKey, 'section', e.target.value)}>
                    <option value="executive">Executive</option>
                    <option value="board">Board Members</option>
                    <option value="management">Management</option>
                  </select>
                </Field>
                <Field><label>Bio</label><textarea value={m.bio || ''} onChange={e=>updateMemberField(memberKey, 'bio', e.target.value)} /></Field>
                {m.image ? <Img src={m.image} alt={m.name} /> : null}
                <Row>
                  <ImagePicker onPick={(b)=>updateMemberField(memberKey, 'image', b)} />
                  <Primary
                    onClick={() => saveMember(memberKey)}
                    disabled={savingMemberId === memberKey}
                  >
                    {savingMemberId === memberKey ? 'Saving…' : 'Save Member'}
                  </Primary>
                  <Danger
                    onClick={()=>removeMember(memberKey)}
                    disabled={removingMemberId === memberKey}
                  >
                    {removingMemberId === memberKey ? 'Deleting…' : 'Delete'}
                  </Danger>
                </Row>
              </Card>
            );
          })}
        </Grid>
      </Card>
    </>
  );
};

const GalleryModule = () => {
  const [page, setPage] = useState(null);
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState('');
  const [category, setCategory] = useState('');
  const [removingId, setRemovingId] = useState(null);
  const [savingPage, setSavingPage] = useState(false);
  const [pageMessage, setPageMessage] = useState(null);

  const load = async () => {
    const [pg, list] = await Promise.all([
      api.gallery.getPage().catch(() => null),
      api.gallery.list()
    ]);
    setPage(pg || { description: '', headerImage: '' });
    setItems(list);
  };
  useEffect(() => { load(); }, []);

  const savePage = async () => {
    setPageMessage(null);
    setSavingPage(true);
    try {
      const d = await api.gallery.updatePage(page);
      setPage(d);
      setPageMessage('Page saved');
    } catch (err) {
      setPageMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to save page');
    } finally {
      setSavingPage(false);
    }
  };

  const create = async (base64) => {
    const d = await api.gallery.create({ image: base64, caption, category });
    setItems(prev => [d, ...prev]);
    setCaption(''); setCategory('');
  };

  const bulk = async (files) => {
    const arr = await Promise.all(Array.from(files).map(async (f) => {
      const res = await api.upload.image(f);
      return { image: res.base64, caption: '', category: '' };
    }));
    await api.gallery.bulk(arr);
    load();
  };

  const update = async (id, field, value) => {
    const it = items.find(x => x._id === id);
    if (!it) return;
    const d = await api.gallery.update(id, { ...it, [field]: value });
    setItems(prev => prev.map(x => x._id === id ? d : x));
  };
  const remove = async (id) => {
    setRemovingId(id);
    try {
      await api.gallery.delete(id);
      setItems(prev => prev.filter(x => x._id !== id));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <>
      <Card>
        <h2>Gallery Page</h2>
        <Field><label>Description</label><textarea value={page?.description || ''} onChange={e=>setPage({ ...page, description: e.target.value })} /></Field>
        {page?.headerImage ? <Img src={page.headerImage} alt="header" /> : null}
        <Row style={{ marginTop: 8 }}>
          <ImagePicker onPick={(b)=> setPage({ ...page, headerImage: b })} />
        </Row>
        <Row style={{ marginTop: 10 }}>
          <Primary onClick={savePage} disabled={savingPage}>
            {savingPage ? 'Saving…' : 'Save Page'}
          </Primary>
          {pageMessage ? <div>{pageMessage}</div> : null}
        </Row>
      </Card>
      <Card>
        <h2>Add Item</h2>
        <Grid>
          <Field><label>Caption</label><input value={caption} onChange={e=>setCaption(e.target.value)} /></Field>
          <Field><label>Category</label><input value={category} onChange={e=>setCategory(e.target.value)} /></Field>
        </Grid>
        <Row>
          <input type="file" accept="image/*" onChange={async e => { const f = e.target.files?.[0]; if (!f) return; const res = await api.upload.image(f); await create(res.base64); e.target.value=''; }} />
          <input type="file" accept="image/*" multiple onChange={e => { const files = e.target.files; if (files?.length) bulk(files); e.target.value=''; }} />
        </Row>
      </Card>
      <Grid>
        {items.map(it => (
          <Card key={it._id}>
            {it.image ? <Img src={it.image} alt={it.caption || 'gallery'} /> : null}
            <Field><label>Caption</label><input value={it.caption || ''} onChange={e=>update(it._id, 'caption', e.target.value)} /></Field>
            <Field><label>Category</label><input value={it.category || ''} onChange={e=>update(it._id, 'category', e.target.value)} /></Field>
            <Row>
              <Danger
                onClick={()=>remove(it._id)}
                disabled={removingId === it._id}
              >
                {removingId === it._id ? 'Deleting…' : 'Delete'}
              </Danger>
            </Row>
          </Card>
        ))}
      </Grid>
    </>
  );
};

const PartnershipsModule = () => {
  const [page, setPage] = useState(null);
  const [partners, setPartners] = useState([]);
  const [message, setMessage] = useState(null);
  const [savingPage, setSavingPage] = useState(false);
  const [savingPartnerId, setSavingPartnerId] = useState(null);
  const [removingPartnerId, setRemovingPartnerId] = useState(null);

  const load = async () => {
    const [p, list] = await Promise.all([api.partnerships.getPage(), api.partners.list()]);
    setPage(p); setPartners(list);
  };
  useEffect(() => { load(); }, []);

  if (!page) return <div>Loading…</div>;

  const savePage = async () => {
    setMessage(null);
    setSavingPage(true);
    try {
      const d = await api.partnerships.updatePage(page);
      setPage(d);
      setMessage('Changes saved');
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to save changes');
    } finally {
      setSavingPage(false);
    }
  };

  const create = () => {
    setMessage(null);
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const draft = { _id: tempId, name: '', image: '', link: '', section: 'advisory', _isNew: true };
    setPartners(prev => [draft, ...prev]);
  };

  const updateLocal = (id, field, value) => {
    setPartners(prev => prev.map(x => x._id === id ? { ...x, [field]: value } : x));
  };

  const savePartner = async (id) => {
    const it = partners.find(x => x._id === id);
    if (!it) return;
    if (!it.name || !it.name.trim()) {
      setMessage('Name is required');
      return;
    }
    setMessage(null);
    setSavingPartnerId(id);
    try {
      const payload = { name: it.name, image: it.image || '', link: it.link || '', section: it.section || 'advisory' };
      let saved;
      if (it._isNew) {
        saved = await api.partners.create(payload);
      } else {
        saved = await api.partners.update(id, payload);
      }
      setPartners(prev => prev.map(x => x._id === id ? saved : x));
      setMessage('Partner saved');
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to save partner');
    } finally {
      setSavingPartnerId(null);
    }
  };

  const remove = async (id) => {
    const it = partners.find(x => x._id === id);
    if (!it) return;
    if (it._isNew) {
      setPartners(prev => prev.filter(x => x._id !== id));
      return;
    }
    setRemovingPartnerId(id);
    try {
      await api.partners.delete(id);
      setPartners(prev => prev.filter(x => x._id !== id));
    } finally {
      setRemovingPartnerId(null);
    }
  };

  return (
    <>
      <Card>
        <h2>Partnership Page</h2>
        <Grid>
          <Field><label>Title</label><input value={page.title || ''} onChange={e=>setPage({ ...page, title: e.target.value })} /></Field>
          <Field><label>Description</label><textarea value={page.description || ''} onChange={e=>setPage({ ...page, description: e.target.value })} /></Field>
        </Grid>
        {page.image ? <Img src={page.image} alt="header" /> : null}
        <Row style={{ marginTop:8 }}>
          <ImagePicker onPick={(b)=> setPage({ ...page, image: b })} />
        </Row>
        <Row style={{ marginTop:10 }}>
          <Primary onClick={savePage} disabled={savingPage}>
            {savingPage ? 'Saving…' : 'Save Page'}
          </Primary>
          {message ? <div>{message}</div> : null}
        </Row>
      </Card>

      <Card>
        <h2>Partners</h2>
        <Row style={{ marginBottom: 10 }}>
          <Primary onClick={create}>
            Add Partner
          </Primary>
        </Row>
        <Grid>
          {partners.map(pt => (
            <Card key={pt._id}>
              <Field><label>Name</label><input value={pt.name || ''} onChange={e=>updateLocal(pt._id, 'name', e.target.value)} /></Field>
              <Field>
                <label>Section</label>
                <select value={pt.section || 'advisory'} onChange={e=>updateLocal(pt._id, 'section', e.target.value)}>
                  <option value="advisory">Advisory Committee</option>
                  <option value="scientific">Scientific Committee</option>
                  <option value="partners">Partners</option>
                </select>
              </Field>
              {pt.image ? <Img src={pt.image} alt={pt.name} /> : null}
              <Row>
                <ImagePicker onPick={(b)=>updateLocal(pt._id, 'image', b)} />
              </Row>
              <Field><label>Link</label><input value={pt.link || ''} onChange={e=>updateLocal(pt._id, 'link', e.target.value)} /></Field>
              <Row>
                <Primary
                  onClick={()=>savePartner(pt._id)}
                  disabled={savingPartnerId === pt._id}
                >
                  {savingPartnerId === pt._id ? 'Saving…' : 'Save'}
                </Primary>
                <Danger
                  onClick={()=>remove(pt._id)}
                  disabled={removingPartnerId === pt._id}
                >
                  {removingPartnerId === pt._id ? 'Deleting…' : 'Delete'}
                </Danger>
              </Row>
            </Card>
          ))}
        </Grid>
      </Card>
    </>
  );
};

const ContactModule = () => {
  const [doc, setDoc] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [p, list] = await Promise.all([api.contact.getPage(), api.contact.listMessages()]);
    setDoc(p); setMessages(list);
  };
  useEffect(() => { load(); }, []);

  if (!doc) return <div>Loading…</div>;

  const platformOptions = [
    { value: 'facebook', label: 'Facebook' },
    { value: 'twitter', label: 'Twitter' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'website', label: 'Website/Other' }
  ];

  const addSocialLink = () => {
    setDoc(prev => ({
      ...prev,
      socialLinks: [...(prev.socialLinks || []), { platform: 'facebook', url: '', label: '' }]
    }));
  };

  const updateSocialLink = (index, field, value) => {
    setDoc(prev => {
      const existing = prev.socialLinks || [];
      const next = existing.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      );
      return { ...prev, socialLinks: next };
    });
  };

  const removeSocialLink = (index) => {
    setDoc(prev => {
      const existing = prev.socialLinks || [];
      const next = existing.filter((_, idx) => idx !== index);
      return { ...prev, socialLinks: next };
    });
  };

  const socialLinks = Array.isArray(doc.socialLinks) ? doc.socialLinks : [];

  const save = async () => {
    setMessage(null);
    setSaving(true);
    try {
      const d = await api.contact.updatePage(doc);
      setDoc(d);
      setMessage('Changes saved');
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card>
        <h2>Contact Content</h2>
        <Grid>
          <Field><label>Title</label><input value={doc.title || ''} onChange={e=>setDoc({ ...doc, title: e.target.value })} /></Field>
          <Field><label>Description</label><textarea value={doc.description || ''} onChange={e=>setDoc({ ...doc, description: e.target.value })} /></Field>
          <Field><label>Address</label><input value={doc.address || ''} onChange={e=>setDoc({ ...doc, address: e.target.value })} /></Field>
          <Field><label>Email</label><input value={doc.email || ''} onChange={e=>setDoc({ ...doc, email: e.target.value })} /></Field>
          <Field><label>Phone</label><input value={doc.phone || ''} onChange={e=>setDoc({ ...doc, phone: e.target.value })} /></Field>
          <Field><label>Hotline Phone</label><input value={doc.hotlinePhone || ''} onChange={e=>setDoc({ ...doc, hotlinePhone: e.target.value })} /></Field>
          <Field><label>Hours of Operation</label><textarea value={doc.hoursOfOperation || ''} onChange={e=>setDoc({ ...doc, hoursOfOperation: e.target.value })} /></Field>
        </Grid>
        <div style={{ marginTop: 16 }}>
          <h3>Social Media Links</h3>
          {socialLinks.length === 0 ? (
            <div style={{ marginBottom: 8 }}>No social links yet. Add one below.</div>
          ) : (
            socialLinks.map((link, idx) => (
              <Grid key={idx}>
                <Field>
                  <label>Platform</label>
                  <select
                    value={link.platform || ''}
                    onChange={e => updateSocialLink(idx, 'platform', e.target.value)}
                  >
                    <option value="">Select platform</option>
                    {platformOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </Field>
                <Field>
                  <label>URL</label>
                  <input
                    value={link.url || ''}
                    onChange={e => updateSocialLink(idx, 'url', e.target.value)}
                    placeholder="https://..."
                  />
                </Field>
                <Field>
                  <label>Label (optional)</label>
                  <input
                    value={link.label || ''}
                    onChange={e => updateSocialLink(idx, 'label', e.target.value)}
                    placeholder="Facebook, X, Instagram…"
                  />
                </Field>
                <Field>
                  <label>&nbsp;</label>
                  <Danger onClick={() => removeSocialLink(idx)}>Remove</Danger>
                </Field>
              </Grid>
            ))
          )}
          <Row style={{ marginTop: 8 }}>
            <Primary type="button" onClick={addSocialLink}>
              Add Social Link
            </Primary>
          </Row>
        </div>
        {doc.image ? <Img src={doc.image} alt="header" /> : null}
        <Row style={{ marginTop:8 }}>
          <ImagePicker onPick={(b)=> setDoc({ ...doc, image: b })} />
        </Row>
        <Row style={{ marginTop:10 }}>
          <Primary onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Primary>
          {message ? <div>{message}</div> : null}
        </Row>
      </Card>

      <Card>
        <h2>Messages</h2>
        {messages.length === 0 ? 'No messages yet.' : (
          <Grid>
            {messages.map(m => (
              <Card key={m._id}>
                <div><strong>{m.name}</strong> • {m.email}</div>
                <div style={{ marginTop: 6 }}>{m.message}</div>
              </Card>
            ))}
          </Grid>
        )}
      </Card>
    </>
  );
};

const UsersModule = ({ me }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ username:'', password:'', role:'admin' });
  const [pwd, setPwd] = useState({ currentPassword:'', newPassword:'' });
  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState(null);
  const [updatingPwd, setUpdatingPwd] = useState(false);
  const [pwdMessage, setPwdMessage] = useState(null);

  const load = () => {
    setLoading(true);
    api.users.list().then(setList).catch(e => setErr(e.message)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreateMessage(null);
    setCreating(true);
    try {
      await api.users.create(form);
      setForm({ username:'', password:'', role:'admin' });
      setCreateMessage('User created');
      load();
    } catch (e) {
      setCreateMessage(e?.message ? `Error: ${e.message}` : 'Error: Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (u) => {
    await api.users.setActive({ userId: u._id, isActive: !u.isActive });
    load();
  };

  const updatePassword = async () => {
    if (!pwd.currentPassword || !pwd.newPassword) return;
    setPwdMessage(null);
    setUpdatingPwd(true);
    try {
      await api.users.updatePassword(pwd);
      setPwd({ currentPassword:'', newPassword:'' });
      setPwdMessage('Password updated');
    } catch (e) {
      setPwdMessage(e?.message ? `Error: ${e.message}` : 'Error: Failed to update password');
    } finally {
      setUpdatingPwd(false);
    }
  };

  return (
    <>
      <Card>
        <h2>Create User</h2>
        {me?.role === 'superAdmin' ? (
          <>
            <Grid>
              <Field><label>Username</label><input value={form.username} onChange={e=>setForm({...form, username:e.target.value})} /></Field>
              <Field><label>Password</label><input type="password" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} /></Field>
              <Field>
                <label>Role</label>
                <select value={form.role} onChange={e=>setForm({...form, role:e.target.value})}>
                  <option value="admin">Admin</option>
                  <option value="superAdmin">Super Admin</option>
                  <option value="foodBusiness">Food Business</option>
                  <option value="communications">Communications</option>
                  <option value="dataEntry">Data Entry</option>
                </select>
              </Field>
            </Grid>
            <Primary onClick={create} disabled={creating}>
              {creating ? 'Creating…' : 'Create'}
            </Primary>
            {createMessage ? <div style={{ marginTop: 8 }}>{createMessage}</div> : null}
          </>
        ) : <div>Only super admin can create users.</div>}
      </Card>
      <Card>
        <h2>Users</h2>
        {loading ? 'Loading…' : err ? err : (
          <Grid>
            {list.map(u => (
              <Card key={u._id}>
                <div><strong>{u.username}</strong></div>
                <div>Role: {u.role}</div>
                <div>Status: {u.isActive ? 'Active' : 'Inactive'}</div>
                {me?.role === 'superAdmin' ? (
                  <Row>
                    <Button onClick={()=>toggle(u)}>{u.isActive ? 'Deactivate' : 'Activate'}</Button>
                  </Row>
                ) : null}
              </Card>
            ))}
          </Grid>
        )}
      </Card>
      <Card>
        <h2>Update My Password</h2>
        <Grid>
          <Field><label>Current Password</label><input type="password" value={pwd.currentPassword} onChange={e=>setPwd({...pwd, currentPassword:e.target.value})} /></Field>
          <Field><label>New Password</label><input type="password" value={pwd.newPassword} onChange={e=>setPwd({...pwd, newPassword:e.target.value})} /></Field>
        </Grid>
        <Primary onClick={updatePassword} disabled={updatingPwd}>
          {updatingPwd ? 'Updating…' : 'Update Password'}
        </Primary>
        {pwdMessage ? <div style={{ marginTop: 8 }}>{pwdMessage}</div> : null}
      </Card>
    </>
  );
};

const HomeModule = () => {
  const [doc, setDoc] = useState(null);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removingHeroBackground, setRemovingHeroBackground] = useState(false);
  const [removingHeroIndex, setRemovingHeroIndex] = useState(null);

  const load = () => api.home.get().then(setDoc);
  useEffect(() => { load(); }, []);

  const save = async () => {
    setMessage(null);
    setSaving(true);
    try {
      const d = await api.home.update(doc);
      setDoc(d);
      setMessage('Changes saved');
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const addHeroImage = (base64) => {
    setDoc(prev => ({ ...prev, hero: { ...prev.hero, images: [...(prev.hero?.images||[]), base64].slice(0,4) } }));
  };

  const removeHeroImage = async (idx) => {
    setRemovingHeroIndex(idx);
    setMessage(null);
    try {
      await api.home.deleteHeroImage(idx);
      await load();
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to remove hero image');
    } finally {
      setRemovingHeroIndex(null);
    }
  };

  const removeHeroBackground = async () => {
    if (!doc?.hero?.backgroundImage) return;
    setRemovingHeroBackground(true);
    setMessage(null);
    try {
      const payload = {
        ...doc,
        hero: {
          ...doc.hero,
          backgroundImage: '',
        },
      };
      const updated = await api.home.update(payload);
      setDoc(updated);
      setMessage('Hero background removed');
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to remove hero background');
    } finally {
      setRemovingHeroBackground(false);
    }
  };

  if (!doc) return <div>Loading…</div>;

  return (
    <>
      <Card>
        <h2>Header</h2>
        <Field><label>Header Title</label><input value={doc.headerTitle || ''} onChange={e=>setDoc({ ...doc, headerTitle: e.target.value })} /></Field>
      </Card>
      <Card>
        <h2>Hero</h2>
        <Grid>
          <Field>
            <label>Title</label>
            <input
              value={doc.hero?.title || ''}
              onChange={e=>setDoc({ ...doc, hero: { ...doc.hero, title: e.target.value } })}
            />
          </Field>
          <Field>
            <label>Subtitle</label>
            <textarea
              value={doc.hero?.subtitle || ''}
              onChange={e=>setDoc({ ...doc, hero: { ...doc.hero, subtitle: e.target.value } })}
            />
          </Field>
        </Grid>
        <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 600 }}>
          Hero background image (fallback)
        </div>
        {doc.hero?.backgroundImage ? (
          <div style={{ marginTop: 10 }}>
            <Img src={doc.hero.backgroundImage} alt="Hero background" />
          </div>
        ) : null}
        <Row style={{ marginTop: 8 }}>
          <ImagePicker onPick={(b)=> setDoc(prev => ({ ...prev, hero: { ...prev.hero, backgroundImage: b } }))} />
          {doc.hero?.backgroundImage ? (
            <Danger onClick={removeHeroBackground} disabled={removingHeroBackground}>
              {removingHeroBackground ? 'Removing…' : 'Remove Background'}
            </Danger>
          ) : null}
        </Row>
        <Row>
          {(doc.hero?.images||[]).map((src, idx) => (
            <div key={idx} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
              <Img src={src} alt={`Hero ${idx+1}`} />
              <Danger onClick={()=>removeHeroImage(idx)} disabled={removingHeroIndex === idx}>
                {removingHeroIndex === idx ? 'Removing…' : 'Remove'}
              </Danger>
            </div>
          ))}
        </Row>
        <Row>
          <ImagePicker onPick={addHeroImage} />
        </Row>
      </Card>
      <Card>
        <h2>Snippets</h2>
        <Grid>
          <Field><label>About</label><textarea value={doc.snippets?.about || ''} onChange={e=>setDoc({ ...doc, snippets: { ...doc.snippets, about: e.target.value } })} /></Field>
          <Field><label>News</label><textarea value={doc.snippets?.news || ''} onChange={e=>setDoc({ ...doc, snippets: { ...doc.snippets, news: e.target.value } })} /></Field>
          <Field><label>Objectives</label><textarea value={doc.snippets?.objectives || ''} onChange={e=>setDoc({ ...doc, snippets: { ...doc.snippets, objectives: e.target.value } })} /></Field>
          <Field><label>Gallery</label><textarea value={doc.snippets?.gallery || ''} onChange={e=>setDoc({ ...doc, snippets: { ...doc.snippets, gallery: e.target.value } })} /></Field>
        </Grid>
      </Card>
      <Card>
        <h2>Contact Info</h2>
        <Grid>
          <Field><label>Address</label><input value={doc.contactInfo?.address || ''} onChange={e=>setDoc({ ...doc, contactInfo: { ...doc.contactInfo, address: e.target.value } })} /></Field>
          <Field><label>Email</label><input value={doc.contactInfo?.email || ''} onChange={e=>setDoc({ ...doc, contactInfo: { ...doc.contactInfo, email: e.target.value } })} /></Field>
          <Field><label>Phone</label><input value={doc.contactInfo?.phone || ''} onChange={e=>setDoc({ ...doc, contactInfo: { ...doc.contactInfo, phone: e.target.value } })} /></Field>
        </Grid>
      </Card>
      <Card>
        <h2>Footer</h2>
        <Grid>
          <Field><label>Address</label><input value={doc.footer?.address || ''} onChange={e=>setDoc({ ...doc, footer: { ...doc.footer, address: e.target.value } })} /></Field>
          <Field><label>Email</label><input value={doc.footer?.email || ''} onChange={e=>setDoc({ ...doc, footer: { ...doc.footer, email: e.target.value } })} /></Field>
          <Field><label>Phone</label><input value={doc.footer?.phone || ''} onChange={e=>setDoc({ ...doc, footer: { ...doc.footer, phone: e.target.value } })} /></Field>
        </Grid>
      </Card>
      <Row>
        <Primary onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Primary>
        {message ? <div>{message}</div> : null}
      </Row>
    </>
  );
};

const AboutModule = () => {
  const [doc, setDoc] = useState(null);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.about.get().then(setDoc);
  useEffect(() => { load(); }, []);

  const save = async () => {
    setMessage(null);
    setSaving(true);
    try {
      const d = await api.about.update(doc);
      setDoc(d);
      setMessage('Changes saved');
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const setImg = (field, base64) => setDoc(prev => ({ ...prev, images: { ...(prev?.images||{}), [field]: base64 } }));
  const delImg = async (field) => { await api.about.deleteImage(field); load(); };

  const addSection = () => setDoc(prev => ({ ...prev, additionalSections: [ ...(prev?.additionalSections||[]), { title:'', content:'', type:'text', image:'' } ] }));
  const removeSection = (idx) => setDoc(prev => ({ ...prev, additionalSections: (prev?.additionalSections||[]).filter((_,i)=>i!==idx) }));

  if (!doc) return <div>Loading…</div>;

  return (
    <>
      <Card>
        <h2>About Content</h2>
        <Grid>
          <Field><label>Bio</label><textarea value={doc.bio || ''} onChange={e=>setDoc({ ...doc, bio: e.target.value })} /></Field>
          <Field><label>Mission</label><textarea value={doc.mission || ''} onChange={e=>setDoc({ ...doc, mission: e.target.value })} /></Field>
          <Field><label>Vision</label><textarea value={doc.vision || ''} onChange={e=>setDoc({ ...doc, vision: e.target.value })} /></Field>
          <Field><label>Goals (one per line; rendered as a dotted list)</label><textarea value={doc.goals || ''} onChange={e=>setDoc({ ...doc, goals: e.target.value })} /></Field>
        </Grid>
      </Card>
      <Card>
        <h2>Section Images</h2>
        <Grid>
          <div>
            <div style={{ fontWeight:600, marginBottom:6 }}>Bio Image</div>
            {doc.images?.bioImage ? <Img src={doc.images.bioImage} alt="bio" /> : null}
            <Row style={{ marginTop:8 }}>
              <ImagePicker onPick={(b)=>setImg('bioImage', b)} />
              {doc.images?.bioImage ? <Danger onClick={()=>delImg('bioImage')}>Remove</Danger> : null}
            </Row>
          </div>
          <div>
            <div style={{ fontWeight:600, marginBottom:6 }}>Mission Image</div>
            {doc.images?.missionImage ? <Img src={doc.images.missionImage} alt="mission" /> : null}
            <Row style={{ marginTop:8 }}>
              <ImagePicker onPick={(b)=>setImg('missionImage', b)} />
              {doc.images?.missionImage ? <Danger onClick={()=>delImg('missionImage')}>Remove</Danger> : null}
            </Row>
          </div>
          <div>
            <div style={{ fontWeight:600, marginBottom:6 }}>Vision Image</div>
            {doc.images?.visionImage ? <Img src={doc.images.visionImage} alt="vision" /> : null}
            <Row style={{ marginTop:8 }}>
              <ImagePicker onPick={(b)=>setImg('visionImage', b)} />
              {doc.images?.visionImage ? <Danger onClick={()=>delImg('visionImage')}>Remove</Danger> : null}
            </Row>
          </div>
          <div>
            <div style={{ fontWeight:600, marginBottom:6 }}>Goals Image</div>
            {doc.images?.goalsImage ? <Img src={doc.images.goalsImage} alt="goals" /> : null}
            <Row style={{ marginTop:8 }}>
              <ImagePicker onPick={(b)=>setImg('goalsImage', b)} />
              {doc.images?.goalsImage ? <Danger onClick={()=>delImg('goalsImage')}>Remove</Danger> : null}
            </Row>
          </div>
        </Grid>
      </Card>
      <Card>
        <h2>Additional Sections</h2>
        <Row style={{ marginBottom:10 }}>
          <Primary onClick={addSection}>Add Section</Primary>
        </Row>
        <Grid>
          {(doc.additionalSections||[]).map((s, idx) => (
            <Card key={idx}>
              <Field><label>Title</label><input value={s.title || ''} onChange={e=>{
                const v = e.target.value; setDoc(prev => ({ ...prev, additionalSections: prev.additionalSections.map((x,i)=> i===idx ? { ...x, title: v } : x) }));
              }} /></Field>
              <Field>
                <label>Section Type</label>
                <select value={s.type || 'text'} onChange={e=>{
                  const v = e.target.value; setDoc(prev => ({ ...prev, additionalSections: prev.additionalSections.map((x,i)=> i===idx ? { ...x, type: v } : x) }));
                }}>
                  <option value="text">Regular text block</option>
                  <option value="list">Dotted list (one item per line)</option>
                </select>
              </Field>
              <Field><label>Content{(s.type || 'text') === 'list' ? ' (one item per line)' : ''}</label><textarea value={s.content || ''} onChange={e=>{
                const v = e.target.value; setDoc(prev => ({ ...prev, additionalSections: prev.additionalSections.map((x,i)=> i===idx ? { ...x, content: v } : x) }));
              }} /></Field>
              {s.image ? <Img src={s.image} alt={s.title||'section'} /> : null}
              <Row>
                <ImagePicker onPick={(b)=> setDoc(prev => ({ ...prev, additionalSections: prev.additionalSections.map((x,i)=> i===idx ? { ...x, image: b } : x) }))} />
                {s.image ? <Danger onClick={()=> setDoc(prev => ({ ...prev, additionalSections: prev.additionalSections.map((x,i)=> i===idx ? { ...x, image: '' } : x) }))}>Remove Image</Danger> : null}
                <Danger onClick={()=>removeSection(idx)}>Delete Section</Danger>
              </Row>
            </Card>
          ))}
        </Grid>
      </Card>
      <Row>
        <Primary onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Primary>
        {message ? <div>{message}</div> : null}
      </Row>
    </>
  );
};

const ObjectivesModule = () => {
  const [doc, setDoc] = useState(null);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.objectives.get().then(setDoc);
  useEffect(() => { load(); }, []);

  const save = async () => {
    setMessage(null);
    setSaving(true);
    try {
      const d = await api.objectives.update(doc);
      setDoc(d);
      setMessage('Changes saved');
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const addObjective = () => setDoc(prev => ({ ...prev, objectives: [ ...(prev?.objectives||[]), { title:'', description:'', image:'' } ] }));
  const removeObjective = (idx) => setDoc(prev => ({ ...prev, objectives: (prev?.objectives||[]).filter((_,i)=>i!==idx) }));
  const delObjectiveImage = async (idx) => { await api.objectives.deleteObjectiveImage(idx); load(); };

  const addSection = () => setDoc(prev => ({ ...prev, sections: [ ...(prev?.sections||[]), { title:'', content:'', type:'text', image:'' } ] }));
  const removeSection = (idx) => setDoc(prev => ({ ...prev, sections: (prev?.sections||[]).filter((_,i)=>i!==idx) }));
  const delSectionImage = async (idx) => { await api.objectives.deleteSectionImage(idx); load(); };

  if (!doc) return <div>Loading…</div>;

  return (
    <>
      <Card>
        <h2>Intro</h2>
        <Field><label>Intro</label><textarea value={doc.intro || ''} onChange={e=>setDoc({ ...doc, intro: e.target.value })} /></Field>
      </Card>
      <Card>
        <h2>Objectives</h2>
        <Row style={{ marginBottom:10 }}>
          <Primary onClick={addObjective}>Add Objective</Primary>
        </Row>
        <Grid>
          {(doc.objectives||[]).map((o, idx) => (
            <Card key={idx}>
              <Field><label>Title</label><input value={o.title || ''} onChange={e=>{
                const v = e.target.value; setDoc(prev => ({ ...prev, objectives: prev.objectives.map((x,i)=> i===idx ? { ...x, title: v } : x) }));
              }} /></Field>
              <Field><label>Description</label><textarea value={o.description || ''} onChange={e=>{
                const v = e.target.value; setDoc(prev => ({ ...prev, objectives: prev.objectives.map((x,i)=> i===idx ? { ...x, description: v } : x) }));
              }} /></Field>
              {o.image ? <Img src={o.image} alt={o.title||'objective'} /> : null}
              <Row>
                <ImagePicker onPick={(b)=> setDoc(prev => ({ ...prev, objectives: prev.objectives.map((x,i)=> i===idx ? { ...x, image: b } : x) }))} />
                {o.image ? <Danger onClick={()=> delObjectiveImage(idx)}>Remove Image</Danger> : null}
                <Danger onClick={()=>removeObjective(idx)}>Delete Objective</Danger>
              </Row>
            </Card>
          ))}
        </Grid>
      </Card>
      <Card>
        <h2>Sections</h2>
        <Row style={{ marginBottom:10 }}>
          <Primary onClick={addSection}>Add Section</Primary>
        </Row>
        <Grid>
          {(doc.sections||[]).map((s, idx) => (
            <Card key={idx}>
              <Field><label>Title</label><input value={s.title || ''} onChange={e=>{
                const v = e.target.value; setDoc(prev => ({ ...prev, sections: prev.sections.map((x,i)=> i===idx ? { ...x, title: v } : x) }));
              }} /></Field>
              <Field>
                <label>Section Type</label>
                <select value={s.type || 'text'} onChange={e=>{
                  const v = e.target.value; setDoc(prev => ({ ...prev, sections: prev.sections.map((x,i)=> i===idx ? { ...x, type: v } : x) }));
                }}>
                  <option value="text">Regular text block</option>
                  <option value="list">Dotted list (one item per line)</option>
                </select>
              </Field>
              <Field><label>Content{(s.type || 'text') === 'list' ? ' (one item per line)' : ''}</label><textarea value={s.content || ''} onChange={e=>{
                const v = e.target.value; setDoc(prev => ({ ...prev, sections: prev.sections.map((x,i)=> i===idx ? { ...x, content: v } : x) }));
              }} /></Field>
              {s.image ? <Img src={s.image} alt={s.title||'section'} /> : null}
              <Row>
                <ImagePicker onPick={(b)=> setDoc(prev => ({ ...prev, sections: prev.sections.map((x,i)=> i===idx ? { ...x, image: b } : x) }))} />
                {s.image ? <Danger onClick={()=> delSectionImage(idx)}>Remove Image</Danger> : null}
                <Danger onClick={()=>removeSection(idx)}>Delete Section</Danger>
              </Row>
            </Card>
          ))}
        </Grid>
      </Card>
      <Row>
        <Primary onClick={save}>Save Changes</Primary>
        {message ? <div>{message}</div> : null}
      </Row>
    </>
  );
};

const CareersModule = () => {
  const [view, setView] = useState('jobs');
  const [page, setPage] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [message, setMessage] = useState(null);
  const [savingPage, setSavingPage] = useState(false);
  const [savingJobId, setSavingJobId] = useState(null);
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [filterJobId, setFilterJobId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [updatingAppId, setUpdatingAppId] = useState(null);
  const [deletingAppId, setDeletingAppId] = useState(null);
  const [expandedAppId, setExpandedAppId] = useState(null);

  const loadJobs = async () => {
    const [p, j] = await Promise.all([api.careers.getPage(), api.careers.listJobs()]);
    setPage(p);
    setJobs(j);
  };

  const loadApplications = async () => {
    const params = {};
    if (filterJobId) params.jobId = filterJobId;
    if (filterStatus) params.status = filterStatus;
    const apps = await api.careers.listApplications(params);
    setApplications(apps);
  };

  useEffect(() => { loadJobs(); }, []);
  useEffect(() => { if (view === 'applications') loadApplications(); }, [view, filterJobId, filterStatus]);

  if (!page) return <div>Loading…</div>;

  const savePage = async () => {
    setMessage(null);
    setSavingPage(true);
    try {
      const d = await api.careers.updatePage(page);
      setPage(d);
      setMessage('Page saved');
    } catch (err) {
      setMessage(err?.message || 'Failed to save page');
    } finally {
      setSavingPage(false);
    }
  };

  const createJob = async () => {
    setMessage(null);
    try {
      const j = await api.careers.createJob({
        title: 'New Position',
        department: '',
        description: 'Add job description.',
        requirements: [],
        responsibilities: [],
        type: 'Full Time',
        location: 'Monrovia',
        isActive: true
      });
      setJobs(prev => [j, ...prev]);
      setMessage('Job created');
    } catch (err) {
      setMessage(err?.message || 'Failed to create job');
    }
  };

  const updateJobLocal = (id, field, value) => {
    setJobs(prev => prev.map(j => j._id === id ? { ...j, [field]: value } : j));
  };

  const saveJob = async (id) => {
    const job = jobs.find(j => j._id === id);
    if (!job) return;
    setSavingJobId(id);
    setMessage(null);
    try {
      const d = await api.careers.updateJob(id, job);
      setJobs(prev => prev.map(j => j._id === id ? d : j));
      setMessage('Job saved');
    } catch (err) {
      setMessage(err?.message || 'Failed to save job');
    } finally {
      setSavingJobId(null);
    }
  };

  const deleteJob = async (id) => {
    setDeletingJobId(id);
    try {
      await api.careers.deleteJob(id);
      setJobs(prev => prev.filter(j => j._id !== id));
    } finally {
      setDeletingJobId(null);
    }
  };

  const updateAppStatus = async (id, status) => {
    setUpdatingAppId(id);
    setMessage(null);
    try {
      const updated = await api.careers.updateApplicationStatus(id, { status });
      setApplications(prev => prev.map(a => a._id === id ? updated : a));
    } catch (err) {
      setMessage(err?.message || 'Failed to update status');
    } finally {
      setUpdatingAppId(null);
    }
  };

  const deleteApp = async (id) => {
    setDeletingAppId(id);
    try {
      await api.careers.deleteApplication(id);
      setApplications(prev => prev.filter(a => a._id !== id));
    } finally {
      setDeletingAppId(null);
    }
  };

  const statusColor = (s) => {
    if (s === 'shortlisted') return '#15803d';
    if (s === 'reviewed') return '#ca8a04';
    if (s === 'rejected') return '#dc2626';
    return '#6b7280';
  };

  return (
    <>
      <Row style={{ marginBottom: 14 }}>
        <Primary onClick={() => setView('jobs')} style={{ opacity: view === 'jobs' ? 1 : 0.6 }}>Job Listings</Primary>
        <Primary onClick={() => setView('applications')} style={{ opacity: view === 'applications' ? 1 : 0.6 }}>Applications</Primary>
      </Row>

      {view === 'jobs' && (
        <>
          <Card>
            <h2>Career Page</h2>
            <Grid>
              <Field><label>Title</label><input value={page.title || ''} onChange={e=>setPage({ ...page, title: e.target.value })} /></Field>
              <Field><label>Description</label><textarea value={page.description || ''} onChange={e=>setPage({ ...page, description: e.target.value })} /></Field>
            </Grid>
            {page.headerImage ? <Img src={page.headerImage} alt="header" /> : null}
            <Row style={{ marginTop:8 }}>
              <ImagePicker onPick={(b)=> setPage({ ...page, headerImage: b })} />
            </Row>
            <Row style={{ marginTop:10 }}>
              <Primary onClick={savePage} disabled={savingPage}>{savingPage ? 'Saving…' : 'Save Page'}</Primary>
              {message ? <div>{message}</div> : null}
            </Row>
          </Card>

          <Card>
            <h2>Job Opportunities</h2>
            <Row style={{ marginBottom: 10 }}>
              <Primary onClick={createJob}>Add Job</Primary>
            </Row>
            <Grid>
              {jobs.map(j => (
                <Card key={j._id}>
                  <Field><label>Title</label><input value={j.title || ''} onChange={e=>updateJobLocal(j._id, 'title', e.target.value)} /></Field>
                  <Field><label>Department</label><input value={j.department || ''} onChange={e=>updateJobLocal(j._id, 'department', e.target.value)} placeholder="e.g. Inspections, Lab, Admin" /></Field>
                  <Field><label>Type</label>
                    <select value={j.type || 'Full Time'} onChange={e=>updateJobLocal(j._id, 'type', e.target.value)}>
                      <option>Full Time</option>
                      <option>Part Time</option>
                      <option>Contract</option>
                      <option>Internship</option>
                    </select>
                  </Field>
                  <Field><label>Location</label><input value={j.location || ''} onChange={e=>updateJobLocal(j._id, 'location', e.target.value)} /></Field>
                  <Field><label>Description</label><textarea value={j.description || ''} onChange={e=>updateJobLocal(j._id, 'description', e.target.value)} /></Field>
                  <Field><label>Requirements (one per line)</label>
                    <textarea
                      value={Array.isArray(j.requirements) ? j.requirements.join('\n') : ''}
                      onChange={e=>updateJobLocal(j._id, 'requirements', e.target.value.split('\n'))}
                      placeholder="Bachelor's degree in Food Science&#10;2+ years experience"
                    />
                  </Field>
                  <Field><label>Responsibilities (one per line)</label>
                    <textarea
                      value={Array.isArray(j.responsibilities) ? j.responsibilities.join('\n') : ''}
                      onChange={e=>updateJobLocal(j._id, 'responsibilities', e.target.value.split('\n'))}
                      placeholder="Conduct food safety inspections&#10;Prepare compliance reports"
                    />
                  </Field>
                  <Field><label>Status</label>
                    <select value={j.isActive ? 'active' : 'inactive'} onChange={e=>updateJobLocal(j._id, 'isActive', e.target.value === 'active')}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </Field>
                  <Field><label>Deadline</label><input type="date" value={j.deadline ? j.deadline.split('T')[0] : ''} onChange={e=>updateJobLocal(j._id, 'deadline', e.target.value)} /></Field>

                  {j.image ? <Img src={j.image} alt="Job" /> : null}
                  <Row>
                    <ImagePicker onPick={(b)=>updateJobLocal(j._id, 'image', b)} />
                  </Row>

                  <Row style={{ marginTop: 12 }}>
                    <Primary onClick={()=>saveJob(j._id)} disabled={savingJobId === j._id}>
                      {savingJobId === j._id ? 'Saving…' : 'Save'}
                    </Primary>
                    <Danger onClick={()=>deleteJob(j._id)} disabled={deletingJobId === j._id}>
                      {deletingJobId === j._id ? 'Deleting…' : 'Delete'}
                    </Danger>
                  </Row>
                </Card>
              ))}
            </Grid>
          </Card>
        </>
      )}

      {view === 'applications' && (
        <Card>
          <h2>Applications</h2>
          <Row style={{ marginBottom: 14, gap: 12 }}>
            <Field style={{ marginBottom: 0, minWidth: 180 }}>
              <label>Filter by Job</label>
              <select value={filterJobId} onChange={e => setFilterJobId(e.target.value)}>
                <option value="">All Jobs</option>
                {jobs.map(j => <option key={j._id} value={j._id}>{j.title}</option>)}
              </select>
            </Field>
            <Field style={{ marginBottom: 0, minWidth: 140 }}>
              <label>Filter by Status</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="rejected">Rejected</option>
              </select>
            </Field>
          </Row>

          {message ? <div style={{ marginBottom: 10 }}>{message}</div> : null}

          {applications.length === 0 ? (
            <div style={{ color: '#6b7280', padding: '20px 0' }}>No applications found.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {applications.map(app => (
                <Card key={app._id} style={{ marginBottom: 0, cursor: 'pointer' }} onClick={() => setExpandedAppId(prev => prev === app._id ? null : app._id)}>
                  <Row style={{ justifyContent: 'space-between' }}>
                    <div>
                      <strong>{app.applicantName}</strong>
                      <span style={{ color: '#6b7280', marginLeft: 8, fontSize: '0.9rem' }}>{app.applicantEmail}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: statusColor(app.status), fontWeight: 600, fontSize: '0.85rem', textTransform: 'capitalize' }}>{app.status}</span>
                    </div>
                  </Row>
                  <Row>
                    <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      Position: <strong>{app.job?.title || 'Unknown'}</strong>
                      {app.job?.department ? ` · ${app.job.department}` : ''}
                    </span>
                    <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                      {new Date(app.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </span>
                  </Row>

                  {expandedAppId === app._id && (
                    <div onClick={e => e.stopPropagation()} style={{ marginTop: 10, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                      {app.applicantPhone && <div style={{ marginBottom: 6 }}><strong>Phone:</strong> {app.applicantPhone}</div>}
                      {app.coverLetter && (
                        <div style={{ marginBottom: 6 }}>
                          <strong>Cover Letter:</strong>
                          <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0', color: '#374151', lineHeight: 1.6 }}>{app.coverLetter}</p>
                        </div>
                      )}
                      {app.resume && (
                        <div style={{ marginBottom: 10 }}>
                          <a href={app.resume} download={app.resumeFileName || 'resume'} style={{ color: '#15803d', fontWeight: 600 }}>
                            Download Resume{app.resumeFileName ? ` (${app.resumeFileName})` : ''}
                          </a>
                        </div>
                      )}
                      <Row>
                        <Field style={{ marginBottom: 0, minWidth: 140 }}>
                          <label>Update Status</label>
                          <select
                            value={app.status}
                            onChange={e => updateAppStatus(app._id, e.target.value)}
                            disabled={updatingAppId === app._id}
                          >
                            <option value="pending">Pending</option>
                            <option value="reviewed">Reviewed</option>
                            <option value="shortlisted">Shortlisted</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </Field>
                        <Danger
                          onClick={() => deleteApp(app._id)}
                          disabled={deletingAppId === app._id}
                          style={{ marginTop: 'auto' }}
                        >
                          {deletingAppId === app._id ? 'Deleting…' : 'Delete'}
                        </Danger>
                      </Row>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </Card>
      )}
    </>
  );
};

const FAActModule = () => {
  const [doc, setDoc] = useState(null);
  const [highlightsText, setHighlightsText] = useState('');
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const normalizePdfDataUri = (value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^data:application\/pdf;base64,/i.test(trimmed)) return trimmed;
    if (/^data:[^;]+;base64,/i.test(trimmed)) return '';
    if (/^[A-Za-z0-9+/=\s]+$/.test(trimmed)) {
      return `data:application/pdf;base64,${trimmed.replace(/\s+/g, '')}`;
    }
    return '';
  };

  const load = async () => {
    const data = await api.faAct.get();
    setDoc(data);
    setHighlightsText(Array.isArray(data?.highlights) ? data.highlights.join('\n') : '');
  };

  useEffect(() => { load(); }, []);

  if (!doc) return <div>Loading…</div>;

  const currentPdfDataUri = normalizePdfDataUri(doc.pdfFile);

  const save = async () => {
    setMessage(null);
    setSaving(true);
    try {
      const payload = {
        ...doc,
        highlights: highlightsText
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean)
      };
      const updated = await api.faAct.update(payload);
      setDoc(updated);
      setHighlightsText(Array.isArray(updated?.highlights) ? updated.highlights.join('\n') : '');
      setMessage('FA Act content saved');
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to save FA Act content');
    } finally {
      setSaving(false);
    }
  };

  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setMessage('Error: PDF is too large. Maximum allowed size is 50MB.');
      event.target.value = '';
      return;
    }

    setMessage(null);
    setUploadingPdf(true);
    try {
      const res = await api.upload.pdf(file);
      const nextPdf = normalizePdfDataUri(res.base64);
      if (!nextPdf) {
        throw new Error('Server did not return a valid base64 PDF payload.');
      }
      setDoc(prev => ({
        ...prev,
        pdfFile: nextPdf,
        pdfFileName: res.fileName || file.name
      }));
      setMessage('PDF uploaded. Click Save Changes to publish.');
    } catch (err) {
      setMessage(err?.message ? `Error: ${err.message}` : 'Error: Failed to upload PDF');
    } finally {
      setUploadingPdf(false);
      event.target.value = '';
    }
  };

  return (
    <>
      <Card>
        <h2>FA Act Content</h2>
        <Grid>
          <Field><label>Header Tag</label><input value={doc.headerTag || ''} onChange={e=>setDoc({ ...doc, headerTag: e.target.value })} /></Field>
          <Field><label>Title</label><input value={doc.title || ''} onChange={e=>setDoc({ ...doc, title: e.target.value })} /></Field>
        </Grid>
        <Field><label>Brief</label><textarea value={doc.brief || ''} onChange={e=>setDoc({ ...doc, brief: e.target.value })} /></Field>
        <Field><label>Highlights (one per line)</label><textarea value={highlightsText} onChange={e=>setHighlightsText(e.target.value)} /></Field>

        <Row>
          <input type="file" accept="application/pdf" onChange={handlePdfUpload} />
          {uploadingPdf ? <span>Uploading PDF…</span> : null}
          {currentPdfDataUri ? (
            <>
              <a href={currentPdfDataUri} download={doc.pdfFileName || 'food-authority-act.pdf'} style={{ fontWeight: 600 }}>
                Download current PDF
              </a>
              <Danger onClick={() => setDoc({ ...doc, pdfFile: '', pdfFileName: '' })}>Remove PDF</Danger>
            </>
          ) : null}
        </Row>
      </Card>

      <Row>
        <Primary onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Primary>
        {message ? <div>{message}</div> : null}
      </Row>
    </>
  );
};

const Placeholder = ({ title }) => (
  <Card>
    <h2>{title}</h2>
    <div>Module UI will be implemented next.</div>
  </Card>
);

const Dashboard = () => {
  const { user, logout } = useAuth();
  
  const availableTabs = useMemo(() => {
    if (user?.role === 'communications') return ['News', 'Gallery'];
    if (user?.role === 'dataEntry') return ['Food Premises'];
    return tabs;
  }, [user]);

  const [tab, setTab] = useState(availableTabs[0]);

  useEffect(() => {
    if (!availableTabs.includes(tab)) {
      setTab(availableTabs[0]);
    }
  }, [availableTabs, tab]);

  const Content = useMemo(() => {
    switch (tab) {
      case 'Users': return <UsersModule me={user} />;
      case 'Home': return <HomeModule />;
      case 'About': return <AboutModule />;
      case 'Objectives': return <ObjectivesModule />;
      case 'News': return <NewsModule />;
      case 'Team': return <TeamModule />;
      case 'Services': return <ServicesModule />;
      case 'Gallery': return <GalleryModule />;
      case 'Partnerships': return <PartnershipsModule />;
      case 'Careers': return <CareersModule />;
      case 'FA Act': return <FAActModule />;
      case 'Contact': return <ContactModule />;
      case 'Food Premises': return <FoodPremisesCMS />;
      default: return null;
    }
  }, [tab, user]);

  return (
    <div>
      <Top>
        <Title>Admin Dashboard</Title>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <Link to="/"><Button>Back to Website</Button></Link>
          <Danger onClick={logout}>Logout</Danger>
        </div>
      </Top>
      <Wrap>
        <Side>
          <Menu>
            {availableTabs.map(t => (
              <Item key={t} onClick={()=>setTab(t)} $active={t===tab}>{t}</Item>
            ))}
          </Menu>
        </Side>
        <Main>
          {Content}
        </Main>
      </Wrap>
    </div>
  );
};

export default Dashboard;
